/**
 * Risiko-Sperre — die Prüfung, die einen Kauf verhindert statt ihn zu
 * kommentieren.
 *
 * P0-5 aus dem V6-Audit vom 11.08.2026: Das offene Gesamtrisiko wurde
 * ANGEZEIGT (Risiko-Zeile auf dem Dashboard), aber nirgends erzwungen. Der
 * Übernahme-Dialog prüfte nur Einzeltrade-Risiko und Kapitalbindung — man konnte
 * beliebig viele Positionen buchen, bis das Konto rechnerisch mehrfach
 * verpfändet war.
 *
 * WAS „RISIKO" HIER HEISST: die Summe dessen, was verloren geht, wenn ALLE
 * offenen Positionen gleichzeitig auf ihren Stop laufen. Nicht der Depotwert,
 * nicht die Kapitalbindung — beides ist etwas anderes und wird getrennt
 * geprüft.
 *
 * NUR ECHTES GELD ZÄHLT (`spuren.ts`): Papier- und Experiment-Positionen
 * binden nichts und verbrauchen deshalb kein Limit. Ronni soll ausprobieren
 * können, ohne dass ihm dabei das Konto zugerechnet wird.
 *
 * Diese Datei entscheidet, sie schreibt nicht. Die Durchsetzung liegt an zwei
 * Stellen: im Dialog (sofort sichtbar, mit Grund) und in der Datenbank
 * (Trigger, Migration `20260811..._risiko_sperre.sql`) — denn ein Riegel, der
 * nur im Browser hängt, ist keiner.
 */

import { bindetEchtesGeld, type SpurQuelle } from './spuren';
import { KORRELATION_DOPPELT, KORRELATION_HINWEIS } from './korrelation';

/**
 * Die gesetzten Portfolio-Grenzen an EINER Stelle.
 *
 * Sie stehen bewusst NICHT in `setup-state.ts` bei den gemessenen Schwellen:
 * Dort liegt, was aus einer Rückrechnung stammt und dessen Änderung die
 * Regelversion erhöht. Hier liegt, was gesetzt wurde, weil es die Bauart des
 * Fehlers verlangt. Wer eine dieser Zahlen ändert, ändert kein Signal — er
 * ändert, wie viel Ähnlichkeit im Depot erlaubt ist.
 */
export const RISIKO_STANDARD = {
  maxPositionen: 6,
  maxJeThema: 2,
  earningsSperreTage: 7,
  // Gemessen, nicht geraten — Herkunft steht bei den Konstanten in
  // korrelation.ts und in docs/konzepte/2026-08-12-korrelation-schwelle.md.
  // Korrelation SPERRT nicht (Entscheidung Ronni, 12.08.); beide Werte
  // steuern nur, ob und wie deutlich sie erwähnt wird.
  korrelationHinweis: KORRELATION_HINWEIS,
  korrelationDoppelt: KORRELATION_DOPPELT,
} as const;

/** Was eine offene Position zum Gesamtrisiko beiträgt. */
export interface RisikoPosition extends SpurQuelle {
  symbol: string;
  quantity: number;
  /** Einstiegspreis in Handelswährung. */
  avg_entry_price: number;
  /** Aktuell gültiger Stop in Handelswährung. */
  current_stop: number | null;
  /** Umrechnung Handels- → Kontowährung. */
  fx_rate_at_entry: number | null;
  asset_class?: string | null;
}

export interface RisikoGrenzen {
  /** Kontogröße in Kontowährung. */
  kontogroesse: number;
  /** Maximales offenes Gesamtrisiko in Prozent des Kontos. */
  maxGesamtRisikoPct: number;
  /**
   * Höchstzahl gleichzeitig offener echter Positionen.
   *
   * Herkunft: Portfolio-Simulation vom 11.08.2026 — das Plateau lag zwischen
   * N=6 und N=16; empfohlen wurde das UNTERE Ende (N=6, rund 1.670 € je
   * Position bei 10.000 €). Der Wert ist eine bewusste Setzung, keine
   * gemessene Optimalzahl; deshalb steht er hier und nicht in setup-state.ts
   * bei den gemessenen Schwellen.
   */
  maxPositionen: number;
  /**
   * Höchstzahl offener echter Positionen im SELBEN Thema.
   *
   * Zwei von sechs — ein Drittel des Depots im selben Klumpen. Ebenfalls eine
   * bewusste Setzung, keine gemessene Zahl: Eine Messung bräuchte
   * Sektorzuordnungen über zwanzig Jahre, die es hier nicht gibt. Begründung
   * ist die Bauart des Fehlers, nicht seine Häufigkeit — wenn drei Werte
   * dasselbe Thema haben, fallen sie am selben Tag, und drei Stops lösen
   * gleichzeitig aus. Genau davor schützt der Deckel, unabhängig davon, wie
   * oft es vorkommt.
   */
  maxJeThema: number;
  /**
   * Wie viele KALENDERTAGE vor einem Earnings-Termin nicht gekauft wird.
   *
   * Sieben — das sind rund fünf Handelstage. Kalendertage, weil die Quelle
   * (`v5_earnings_cache`) ein Datum liefert und keinen Börsenkalender; eine
   * Umrechnung in Handelstage wäre hier eine Schätzung, die genauer aussieht,
   * als sie ist.
   *
   * Warum überhaupt: Ein Quartalsbericht ist ein Münzwurf über Nacht, gegen
   * den kein Stop hilft — der Kurs öffnet UNTER dem Stop, nicht auf ihm. Die
   * Zustandsmaschine kennt keine Termine, deshalb muss die Sperre hier greifen.
   */
  earningsSperreTage: number;
  /**
   * Ab hier wird die Ähnlichkeit zum Bestand erwähnt.
   *
   * Sie SPERRT nicht. Jede feste Schwelle greift bevorzugt im Marktstress —
   * bei 0,80 in 32,4 % der Fälle gegenüber 13,7 % im ruhigen Markt — also
   * genau dann, wenn antizyklisch gekauft wird. Ob zwei ähnliche Werte
   * zusammen ins Depot dürfen, entscheidet der Trader.
   */
  korrelationHinweis: number;
  /** Ab hier heißt der Hinweis „faktisch derselbe Wert" statt „läuft ähnlich". */
  korrelationDoppelt: number;
}

/**
 * Was die Prüfung über das Umfeld des neuen Trades weiß.
 *
 * Bewusst als DATEN hereingereicht, nicht hier beschafft: Diese Datei
 * entscheidet und liest nichts. Der Dialog sammelt, der Datenbank-Trigger
 * fragt dieselben Quellen in SQL ab.
 *
 * DER NEUE TRADE GILT ALS ECHT. Klumpen und Earnings werden geprüft, sobald
 * ein Kontext übergeben wird — ohne zu fragen, auf welche Spur gebucht wird.
 * Das ist heute richtig, weil nur der Übernahme-Dialog diese Funktion mit
 * Kontext aufruft und der bucht ausschließlich echtes Geld; Experimente gehen
 * über einen eigenen Weg an ihr vorbei. Wer das ändert, muss die Spur des
 * NEUEN Trades hier mit hereingeben — sonst blockiert eine Earnings-Sperre
 * plötzlich ein Experiment, dessen Zweck genau das Ausprobieren ist.
 *
 * JEDES FELD DARF UNBEKANNT SEIN, und Unbekanntes sperrt NICHT. Das ist eine
 * Abweichung von „fehlende Daten blockieren" (Regel 6) und sie ist bewusst:
 * Dort geht es um das SIGNAL — ein Kaufsignal auf veralteten Kursen ist
 * wertlos. Hier geht es um Zusatzprüfungen. Ein Thema kennt nur, wer den Wert
 * getaggt hat; einen Earnings-Termin hat nur ein Teil der Watchlist. Würde
 * Unbekanntes sperren, wäre ein Drittel der Liste dauerhaft dicht — und die
 * Sperre würde abgeschaltet statt befolgt. Was nicht prüfbar war, steht
 * deshalb im Ergebnis und wird angezeigt.
 */
export interface PortfolioKontext {
  /** Themen des neuen Werts (`theme_tags`). Leer = nicht prüfbar. */
  neuThemen: readonly string[];
  /** Themen je offener Position, Schlüssel ist das Symbol in Großschreibung. */
  themenJeSymbol: Readonly<Record<string, readonly string[]>>;
  /** Kalendertage bis zum nächsten Earnings-Termin. null = unbekannt. */
  tageBisEarnings: number | null;
  /**
   * Die HÖCHSTE Korrelation des neuen Werts zu einer offenen echten Position,
   * samt Partner. `null` = nicht bestimmbar (kein Bestand, zu wenig
   * gemeinsame Historie, oder noch kein Tages-Snapshot).
   *
   * Fertig gerechnet hereingereicht statt hier ermittelt: Die Zahl entsteht
   * einmal im Cron und landet im Snapshot. Dialog und Datenbank-Trigger lesen
   * dieselbe gespeicherte Zahl — sonst gäbe es zwei Rechnungen und damit zwei
   * Antworten auf dieselbe Frage.
   */
  maxKorrelation: { wert: number; gegen: string } | null;
}

/** Was an diesem Trade nicht geprüft werden konnte — Anzeige, keine Sperre. */
export type NichtPruefbar =
  | 'thema_unbekannt'
  | 'earnings_unbekannt'
  | 'korrelation_unbekannt';

export interface RisikoPruefung {
  /** Darf gebucht werden? */
  erlaubt: boolean;
  /** Offenes Risiko der bestehenden echten Positionen, in Prozent. */
  bestehendPct: number;
  /** Was der neue Trade zusätzlich riskiert, in Prozent. */
  neuPct: number;
  /** Summe. */
  gesamtPct: number;
  /** Wie viele echte Positionen bereits offen sind. */
  bestehendeAnzahl: number;
  /** Klartext-Gründe. Leer, wenn erlaubt. */
  gruende: string[];
  /** Maschinenlesbar, für Snapshot und Tests. */
  blockiert_durch: Array<
    | 'gesamtrisiko'
    | 'positionszahl'
    | 'kapital'
    | 'ungueltige_eingabe'
    | 'themenklumpen'
    | 'earnings'
  >;
  /**
   * Was mangels Daten nicht geprüft werden konnte. Kein Sperrgrund, aber es
   * gehört in die Anzeige: „geprüft" und „nicht prüfbar" dürfen nicht gleich
   * aussehen.
   */
  nicht_pruefbar: NichtPruefbar[];
  /**
   * Was auffällt, ohne zu sperren. Getrennt von `gruende`, weil sonst ein
   * Hinweis wie eine Sperre aussähe — und eine Sperre, die man doch umgehen
   * kann, erzieht dazu, alle Sperren zu überlesen.
   */
  hinweise: string[];
}

/**
 * Risiko einer einzelnen Position in Kontowährung: Abstand zum Stop mal
 * Stückzahl mal Wechselkurs.
 *
 * Ohne Stop gibt es KEIN definiertes Risiko. Solche Positionen werden mit
 * ihrem vollen Einsatz gerechnet — nicht mit null. Eine Position ohne Stop
 * als risikofrei zu behandeln wäre die gefährlichste aller Vereinfachungen.
 */
export function risikoBetrag(p: RisikoPosition): number {
  const fx = p.fx_rate_at_entry || 1;
  const menge = Number(p.quantity);
  const einstieg = Number(p.avg_entry_price);
  if (!(menge > 0) || !(einstieg > 0)) return 0;
  const stop = p.current_stop;
  if (stop === null || !(stop > 0) || stop >= einstieg) {
    return einstieg * menge * fx;
  }
  return (einstieg - stop) * menge * fx;
}

/**
 * Was gerade offen im Risiko steht — ohne einen neuen Trade zu betrachten.
 *
 * Dieselbe Zahl, die `pruefeRisiko` als `bestehendPct` liefert; hier
 * herausgezogen, damit die Anzeige auf dem Dashboard nicht ihre eigene
 * Rechnung mitbringt. Die Sperre und die Anzeige müssen dieselbe Zahl nennen,
 * sonst blockiert die Datenbank bei einem Wert, den die Seite als grün zeigt.
 *
 * Nur echtes Geld: Papier und Experiment verbrauchen kein Limit.
 */
export function offenesRisiko(
  bestehende: readonly RisikoPosition[],
  kontogroesse: number
): { betrag: number; prozent: number; anzahl: number } {
  const echte = bestehende.filter(bindetEchtesGeld);
  const betrag = echte.reduce((s, p) => s + risikoBetrag(p), 0);
  return {
    betrag,
    prozent: kontogroesse > 0 ? (betrag / kontogroesse) * 100 : 0,
    anzahl: echte.length,
  };
}

export function pruefeRisiko(
  bestehende: readonly RisikoPosition[],
  neu: { einstieg: number; stop: number; stueck: number; fxRate: number },
  grenzen: RisikoGrenzen,
  /**
   * Optional, damit bestehende Aufrufer und Messskripte unverändert laufen.
   * Fehlt er, entfallen Klumpen- und Earnings-Prüfung ersatzlos — sie werden
   * dann NICHT als „nicht prüfbar" gemeldet, denn niemand hat gefragt.
   */
  kontext?: PortfolioKontext
): RisikoPruefung {
  // Nur echtes Geld. Papier und Experiment verbrauchen kein Limit.
  const echte = bestehende.filter(bindetEchtesGeld);
  const konto = grenzen.kontogroesse;

  const gruende: string[] = [];
  const blockiert: RisikoPruefung['blockiert_durch'] = [];

  if (!(konto > 0)) {
    return {
      erlaubt: false,
      bestehendPct: 0,
      neuPct: 0,
      gesamtPct: 0,
      bestehendeAnzahl: echte.length,
      gruende: ['Kontogröße ist nicht gesetzt — ohne sie lässt sich kein Risiko rechnen.'],
      blockiert_durch: ['ungueltige_eingabe'],
      nicht_pruefbar: [],
      hinweise: [],
    };
  }

  // Dieselbe Funktion, die auch das Dashboard anzeigt — eine Rechnung.
  const { prozent: bestehendPct } = offenesRisiko(echte, konto);

  const neuBetrag =
    neu.stueck > 0 && neu.einstieg > 0 && neu.stop > 0 && neu.stop < neu.einstieg
      ? (neu.einstieg - neu.stop) * neu.stueck * (neu.fxRate || 1)
      : 0;
  const neuPct = (neuBetrag / konto) * 100;
  const gesamtPct = bestehendPct + neuPct;

  if (neuBetrag <= 0) {
    gruende.push('Einstieg, Stop oder Stückzahl ergeben kein rechenbares Risiko.');
    blockiert.push('ungueltige_eingabe');
  }

  if (gesamtPct > grenzen.maxGesamtRisikoPct) {
    gruende.push(
      `Offenes Gesamtrisiko wäre ${gesamtPct.toFixed(2)} % — erlaubt sind ` +
        `${grenzen.maxGesamtRisikoPct.toFixed(2)} %. Bereits offen: ${bestehendPct.toFixed(2)} % ` +
        `aus ${echte.length} ${echte.length === 1 ? 'Position' : 'Positionen'}.`
    );
    blockiert.push('gesamtrisiko');
  }

  if (echte.length >= grenzen.maxPositionen) {
    gruende.push(
      `Schon ${echte.length} echte Positionen offen — mehr als ${grenzen.maxPositionen} ` +
        'sind von Hand kaum zu führen. Erst eine schließen.'
    );
    blockiert.push('positionszahl');
  }

  const kapital = neu.einstieg * neu.stueck * (neu.fxRate || 1);
  if (kapital > konto) {
    gruende.push(
      `Die Position bindet ${kapital.toFixed(0)} — mehr als das ganze Konto (${konto.toFixed(0)}).`
    );
    blockiert.push('kapital');
  }

  const nichtPruefbar: NichtPruefbar[] = [];
  const hinweise: string[] = [];

  if (kontext) {
    // --- Themenklumpen -----------------------------------------------------
    // Gezählt wird je Thema getrennt: Ein Wert mit zwei Tags kann in einem
    // Thema noch Platz haben und im anderen nicht. Gesperrt wird, sobald EIN
    // Thema voll ist — sonst rettet ein Nebentag den Klumpen.
    if (kontext.neuThemen.length === 0) {
      nichtPruefbar.push('thema_unbekannt');
    } else {
      for (const thema of kontext.neuThemen) {
        const gleiche = echte.filter((p) =>
          (kontext.themenJeSymbol[p.symbol.toUpperCase()] ?? []).includes(thema)
        );
        if (gleiche.length >= grenzen.maxJeThema) {
          gruende.push(
            `Thema „${themaLabel(thema)}": schon ${gleiche.length} offene ` +
              `${gleiche.length === 1 ? 'Position' : 'Positionen'} ` +
              `(${gleiche.map((p) => p.symbol).join(', ')}). Erlaubt sind ` +
              `${grenzen.maxJeThema}. Diese Werte fallen am selben Tag — ` +
              'erst eine davon schließen.'
          );
          blockiert.push('themenklumpen');
          break;
        }
      }
    }

    // --- Earnings ----------------------------------------------------------
    // Ein Quartalsbericht springt ÜBER den Stop, nicht auf ihn. Der Abstand
    // wird deshalb vor dem Kauf geprüft und nicht danach beklagt.
    const tage = kontext.tageBisEarnings;
    if (tage === null) {
      nichtPruefbar.push('earnings_unbekannt');
    } else if (tage >= 0 && tage <= grenzen.earningsSperreTage) {
      gruende.push(
        `Earnings in ${tage} ${tage === 1 ? 'Tag' : 'Tagen'}. ` +
          'Über Nacht öffnet der Kurs unter dem Stop statt auf ihm — ' +
          `bis ${grenzen.earningsSperreTage} Tage davor wird nicht gekauft.`
      );
      blockiert.push('earnings');
    }

    // --- Korrelation -------------------------------------------------------
    // Nur der Bestand zählt: Ohne offene Position gibt es nichts zu
    // verdoppeln, und dann ist „nicht bestimmbar" kein Mangel.
    if (echte.length === 0) {
      // kein Hinweis, kein Mangel — es gibt schlicht keinen Partner
    } else if (kontext.maxKorrelation === null) {
      nichtPruefbar.push('korrelation_unbekannt');
    } else {
      const { wert, gegen } = kontext.maxKorrelation;
      if (wert >= grenzen.korrelationDoppelt) {
        hinweise.push(
          `Läuft zu ${(wert * 100).toFixed(0)} % mit ${gegen} gleich — das ist ` +
            'kein zweiter Wert, das ist praktisch derselbe doppelt.'
        );
      } else if (wert >= grenzen.korrelationHinweis) {
        hinweise.push(
          `Läuft zu ${(wert * 100).toFixed(0)} % mit ${gegen} gleich. Die ` +
            'beiden fallen meist an denselben Tagen.'
        );
      }
    }
  }

  return {
    erlaubt: blockiert.length === 0,
    bestehendPct,
    neuPct,
    gesamtPct,
    bestehendeAnzahl: echte.length,
    gruende,
    blockiert_durch: blockiert,
    nicht_pruefbar: nichtPruefbar,
    hinweise,
  };
}

/** Die Tag-Kennung in Alltagssprache. Unbekannte Tags bleiben, wie sie sind. */
function themaLabel(tag: string): string {
  const map: Record<string, string> = {
    theme_ai: 'KI',
    theme_robotics: 'Robotik',
    theme_space: 'Raumfahrt',
    theme_tesla: 'Tesla',
    theme_dividend_aristocrat: 'Dividenden-Aristokraten',
  };
  return map[tag] ?? tag;
}
